import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Trash2, Check, Plus, Package, LayoutGrid, List as ListIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOT_EMOJI = {
  top: '👚', bottom: '👖', dress: '👗', shoes: '👠',
  outer: '🧥', accessory: '💎', bag: '👜', jewelry: '💍', hat: '👒',
};

export default function CapsuleTab() {
  const [capsules, setCapsules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [vibe, setVibe] = useState('');
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'grid'

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/style/capsules`, { withCredentials: true });
      setCapsules(data.items || []);
      if (data.items && data.items[0]) setOpenId((cur) => cur || data.items[0].id);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createCapsule = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/style/capsules`,
        vibe.trim() ? { vibe: vibe.trim() } : {},
        { withCredentials: true }
      );
      setCapsules((list) => [data.item, ...list]);
      setOpenId(data.item.id);
      setVibe('');
      toast.success(`Kapsel skapad: ${data.item.name} ✨`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skapa kapsel');
    } finally { setCreating(false); }
  };

  const toggleAcquire = async (cap, idx, isAcquired) => {
    try {
      const url = isAcquired
        ? `${API}/style/capsules/${cap.id}/unacquire/${idx}`
        : `${API}/style/capsules/${cap.id}/acquire/${idx}`;
      const { data } = await axios.post(url, {}, { withCredentials: true });
      setCapsules((list) => list.map((c) => {
        if (c.id !== cap.id) return c;
        const items = [...c.items];
        items[idx] = data.item;
        return { ...c, items };
      }));
      if (!isAcquired) toast.success('Tillagd i garderoben ✨');
      else toast.success('Markerad som ej införskaffad');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte uppdatera');
    }
  };

  const deleteCapsule = async (cap) => {
    if (!window.confirm(`Ta bort kapseln "${cap.name}"? (Plagg redan i garderoben påverkas inte)`)) return;
    try {
      await axios.delete(`${API}/style/capsules/${cap.id}`, { withCredentials: true });
      setCapsules((list) => list.filter((c) => c.id !== cap.id));
      toast.success('Kapsel borttagen');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar kapslar…</div>;

  return (
    <div className="space-y-4" data-testid="capsule-tab">
      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <h3 className="font-bold font-['Fredoka'] text-lg flex items-center gap-2 mb-2">
          <Package className="w-4 h-4" /> Skapa kapselgarderob
        </h3>
        <p className="text-xs opacity-75 mb-3">
          En kurerad lista på 12 essentials anpassad till din profil. Kryssa i plagg när du skaffat dem
          — de hamnar automatiskt i din garderob 💛
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={vibe}
            onChange={(e) => setVibe(e.target.value)}
            placeholder="Tema (valfritt) — t.ex. 'resa', 'kontor', 'weekend'"
            maxLength={60}
            className="flex-1 min-w-[200px] px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
            data-testid="capsule-vibe-input"
          />
          <button
            onClick={createCapsule}
            disabled={creating}
            className="neo-btn px-4 py-2 bg-[#B9FBC0] text-sm font-bold flex items-center gap-1 disabled:opacity-50"
            data-testid="capsule-create-btn"
          >
            {creating ? (
              <>
                <Sparkles className="w-4 h-4 animate-spin" /> Genererar…
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Generera kapsel
              </>
            )}
          </button>
        </div>
      </div>

      {capsules.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white dark:bg-[#252545]" data-testid="capsule-empty">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="font-bold">Inga kapslar än</p>
          <p className="text-xs opacity-70 mt-1">Skapa din första kapselgarderob ovan.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs opacity-70">{capsules.length} kapsel{capsules.length === 1 ? '' : 'ar'}</p>
            <div className="flex gap-1">
              <button
                onClick={() => setView('list')}
                className={`neo-btn px-2 py-1 text-xs flex items-center gap-1 ${view === 'list' ? 'bg-[#A2D2FF]' : 'bg-white'}`}
                data-testid="capsule-view-list"
                title="Lista"
              >
                <ListIcon className="w-3 h-3" /> Lista
              </button>
              <button
                onClick={() => setView('grid')}
                className={`neo-btn px-2 py-1 text-xs flex items-center gap-1 ${view === 'grid' ? 'bg-[#A2D2FF]' : 'bg-white'}`}
                data-testid="capsule-view-grid"
                title="Galleri"
              >
                <LayoutGrid className="w-3 h-3" /> Galleri
              </button>
            </div>
          </div>

          {view === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="capsule-grid">
              {capsules.map((cap) => {
                const acquiredCount = (cap.items || []).filter((it) => it.acquired).length;
                const total = (cap.items || []).length;
                const pct = total > 0 ? Math.round((acquiredCount / total) * 100) : 0;
                return (
                  <button
                    key={cap.id}
                    onClick={() => { setView('list'); setOpenId(cap.id); }}
                    className="neo-card p-4 bg-white dark:bg-[#252545] text-left hover:bg-[#FFFAF0] dark:hover:bg-[#2a2a50] transition"
                    data-testid={`capsule-grid-${cap.id}`}
                  >
                    <h3 className="font-bold font-['Fredoka'] text-sm truncate">{cap.name}</h3>
                    <p className="text-[11px] opacity-70 line-clamp-2 mt-0.5">{cap.description}</p>
                    <div className="grid grid-cols-4 gap-1 mt-3">
                      {(cap.items || []).slice(0, 12).map((it, i) => (
                        <div
                          key={i}
                          className={`aspect-square rounded-lg border-2 border-black flex items-center justify-center text-lg ${it.acquired ? 'bg-[#B9FBC0]' : 'bg-[#FFFAF0]'}`}
                          title={it.name}
                        >
                          {SLOT_EMOJI[it.slot] || '✨'}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-3 gap-2">
                      <div className="progress-bar flex-1" style={{ height: '0.4rem' }}>
                        <div className="progress-fill bg-[#B9FBC0]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] font-bold whitespace-nowrap">{acquiredCount}/{total}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
          {capsules.map((cap) => {
            const acquiredCount = (cap.items || []).filter((it) => it.acquired).length;
            const total = (cap.items || []).length;
            const pct = total > 0 ? Math.round((acquiredCount / total) * 100) : 0;
            const isOpen = openId === cap.id;
            return (
              <div key={cap.id} className="neo-card bg-white dark:bg-[#252545] overflow-hidden" data-testid={`capsule-${cap.id}`}>
                <button
                  onClick={() => setOpenId(isOpen ? null : cap.id)}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold font-['Fredoka'] text-lg truncate">{cap.name}</h3>
                      {cap.description && (
                        <p className="text-xs opacity-75 line-clamp-2">{cap.description}</p>
                      )}
                    </div>
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#FDF0D5] border-2 border-black whitespace-nowrap">
                      {acquiredCount}/{total} · {pct}%
                    </span>
                  </div>
                  <div className="progress-bar mt-2" style={{ height: '0.5rem' }}>
                    <div className="progress-fill bg-[#B9FBC0]" style={{ width: `${pct}%` }} />
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t-2 border-black p-4 space-y-2 bg-[#FFFAF0] dark:bg-[#1f1f3a]">
                    {(cap.items || []).map((it, i) => (
                      <div
                        key={i}
                        className={`rounded-xl border-2 border-black p-3 transition-all ${it.acquired ? 'bg-[#B9FBC0]/40' : 'bg-white dark:bg-[#2d2d54]'}`}
                        data-testid={`capsule-item-${cap.id}-${i}`}
                      >
                        <div className="flex items-start gap-2">
                          <button
                            onClick={() => toggleAcquire(cap, i, it.acquired)}
                            className={`w-7 h-7 rounded-full border-2 border-black flex items-center justify-center flex-shrink-0 ${it.acquired ? 'bg-[#B9FBC0]' : 'bg-white'}`}
                            title={it.acquired ? 'Markera ej införskaffad' : 'Markera som införskaffad'}
                            data-testid={`capsule-toggle-${cap.id}-${i}`}
                          >
                            {it.acquired && <Check className="w-4 h-4" />}
                          </button>
                          <span className="text-xl leading-none">{SLOT_EMOJI[it.slot] || '✨'}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-bold text-sm leading-tight ${it.acquired ? 'line-through opacity-70' : ''}`}>
                              {it.name}
                            </p>
                            {it.color && (
                              <p className="text-[11px] opacity-70 mt-0.5">Färg: <span className="font-semibold">{it.color}</span></p>
                            )}
                            {it.why && <p className="text-xs mt-1 italic opacity-80">💡 {it.why}</p>}
                            {!it.acquired && it.where && it.where.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {it.where.map((w, wi) => (
                                  <span key={wi} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFC8DD] border-2 border-black">
                                    {w}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => deleteCapsule(cap)}
                      className="neo-btn px-3 py-1.5 bg-white text-xs font-bold flex items-center gap-1 ml-auto"
                      data-testid={`capsule-delete-${cap.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Ta bort kapsel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
